import { useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from './useToast';

export const useDepartmentManagement = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const addMembers = async (departmentId: string, userIds: string[]) => {
    setIsLoading(true);
    setError(null);

    try {
      // Update users' department_id
      const { error: updateError } = await supabase
        .from('users')
        .update({ 
          department_id: departmentId,
          updated_at: new Date().toISOString()
        })
        .in('id', userIds);

      if (updateError) throw updateError;

      // Create department_workers entries
      const workerEntries = userIds.map(userId => ({
        department_id: departmentId,
        user_id: userId,
        role: 'member',
        created_at: new Date().toISOString()
      }));

      const { error: insertError } = await supabase
        .from('department_workers')
        .insert(workerEntries);

      if (insertError) {
        // Rollback department_id update if worker entry creation fails
        await supabase
          .from('users')
          .update({ 
            department_id: null,
            updated_at: new Date().toISOString()
          })
          .in('id', userIds);
        
        throw insertError;
      }

      toast({
        title: "Success",
        description: "Members added successfully"
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add members';
      setError(message);
      toast({
        title: "Error",
        description: message,
        variant: "destructive"
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const removeMember = async (departmentId: string, userId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // First remove from department_workers
      const { error: deleteError } = await supabase
        .from('department_workers')
        .delete()
        .eq('department_id', departmentId)
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      // Then update user's department_id
      const { error: updateError } = await supabase
        .from('users')
        .update({ 
          department_id: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .eq('department_id', departmentId);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Member removed successfully"
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove member';
      setError(message);
      toast({
        title: "Error",
        description: message,
        variant: "destructive"
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    addMembers,
    removeMember,
    isLoading,
    error
  };
};